import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import TambaLogo from "@/components/TambaLogo";
import ThemeToggle from "@/components/ThemeToggle";
import { 
  TrendingUp, 
  MessageCircle, 
  Target, 
  PieChart, 
  Shield, 
  Globe,
  ArrowRight,
  Check,
  Sparkles,
  Zap,
  Twitter,
  Instagram,
  Linkedin,
  Facebook,
  Youtube,
  Smartphone,
  CreditCard,
  ShieldCheck,
  PiggyBank,
  Landmark,
  HeartHandshake,
  Home,
  Calculator,
  FileText,
  Briefcase
} from "lucide-react";
import { toast } from "sonner";

const LandingPage = () => {
  const navigate = useNavigate();
  
  // Demo account data
  const DEMO_USER = {
    id: "b118ce76-6cd4-486f-b1fa-29c2f71c50e6",
    phone: "5559876543",
    name: "Alex Johnson",
    email: "alex.johnson@example.com",
    subscription_status: "premium",
    onboarding_completed: true,
    currency: "USD",
    risk_tolerance: 65,
    income_range: "10000-20000",
    current_savings: "20000-50000",
    year_of_birth: "1988",
    is_accredited_investor: true,
    referral_code: "TAMBA-DQ8E3F",
    profile: {
      profession: "Senior Product Manager",
      financial_goals: ["retirement", "house", "investment"]
    }
  };

  const handleTryDemo = () => {
    // Store demo user in localStorage
    localStorage.setItem("tamba_user", JSON.stringify(DEMO_USER));
    toast.success("Welcome to the demo! Explore all features as Alex Johnson.");
    navigate("/dashboard");
  };
  
  const features = [
    {
      icon: MessageCircle,
      title: "AI Financial Advisor",
      description: "Get personalized advice from your AI wealth coach, available 24/7",
      color: "bg-tamba-blue text-white",
      iconBg: "bg-white/20"
    },
    {
      icon: PieChart,
      title: "Smart Budget Tracking",
      description: "Automatically categorize expenses and visualize your spending patterns",
      color: "bg-tamba-yellow text-black",
      iconBg: "bg-black/10"
    },
    {
      icon: Target,
      title: "Goal Achievement",
      description: "Set financial goals and track progress with AI-powered insights",
      color: "bg-tamba-coral text-white",
      iconBg: "bg-white/20"
    },
    {
      icon: TrendingUp,
      title: "Wealth Growth",
      description: "Actionable strategies to save more, spend smarter, and grow wealth",
      color: "bg-tamba-teal text-white",
      iconBg: "bg-white/20"
    },
    {
      icon: Shield,
      title: "Bank-Level Security",
      description: "Your financial data is encrypted and protected at all times",
      color: "bg-slate-700 text-white",
      iconBg: "bg-white/20"
    },
    {
      icon: Globe,
      title: "Global Support",
      description: "Works with any currency, in any market, for anyone",
      color: "bg-tamba-teal text-white",
      iconBg: "bg-white/20"
    }
  ];

  const testimonials = [
    {
      quote: "Tamba helped me save $3,000 in just 3 months by identifying spending leaks I never noticed.",
      author: "Sarah K.",
      role: "Marketing Manager",
      avatar: "S"
    },
    {
      quote: "Finally, financial advice that actually understands my situation. It's like having a private banker in my pocket.",
      author: "James L.",
      role: "Software Engineer",
      avatar: "J"
    },
    {
      quote: "I've tried every budgeting app. Tamba is the only one that actually changed my financial behavior.",
      author: "Maria R.",
      role: "Small Business Owner",
      avatar: "M"
    }
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-background/90 backdrop-blur-xl border-b border-border/30">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center"
          >
            <TambaLogo className="h-12 w-auto" />
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 sm:gap-4"
          >
            <ThemeToggle />
            <Button 
              variant="ghost" 
              onClick={() => navigate("/services")}
              className="font-medium"
              data-testid="nav-services-btn"
            >
              Services
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => navigate("/pricing")}
              className="font-medium"
              data-testid="nav-pricing-btn"
            >
              Pricing
            </Button>
            <Button 
              onClick={() => navigate("/auth")}
              className="rounded-full px-6 bg-tamba-blue hover:bg-tamba-blue/90 shadow-lg shadow-tamba-blue/25"
              data-testid="nav-login-btn"
            >
              Get Started
            </Button>
          </motion.div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 relative">
        {/* Animated background shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="absolute top-20 left-[10%] w-72 h-72 bg-tamba-blue/20 rounded-full blur-3xl"
          />
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="absolute top-40 right-[15%] w-96 h-96 bg-tamba-yellow/15 rounded-full blur-3xl"
          />
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="absolute bottom-20 left-[30%] w-64 h-64 bg-tamba-coral/15 rounded-full blur-3xl"
          />
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1, delay: 0.8 }}
            className="absolute bottom-40 right-[10%] w-48 h-48 bg-tamba-teal/20 rounded-full blur-3xl"
          />
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge */}
            <motion.div 
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-tamba-blue/10 to-tamba-teal/10 border border-tamba-blue/20 rounded-full text-sm font-medium mb-8"
            >
              <Sparkles className="w-4 h-4 text-tamba-yellow" />
              <span className="text-tamba-blue font-semibold">Your AI-Powered Wealth Partner</span>
            </motion.div>
            
            {/* Main Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-foreground leading-tight mb-6">
              Build Wealth with
              <span className="block mt-2">
                <span className="text-tamba-blue">Intelligent</span>{" "}
                <span className="text-tamba-yellow">Financial</span>{" "}
                <span className="text-tamba-coral">Guidance</span>
              </span>
            </h1>
            
            {/* Tagline */}
            <p className="text-lg md:text-xl text-muted-foreground mb-4 max-w-2xl mx-auto">
              AI-powered insights with personalized coaching to help you save more, spend smarter, and achieve your financial dreams.
            </p>
            
            {/* Just Dream tagline */}
            <p className="text-sm font-medium text-tamba-teal mb-10 tracking-widest uppercase">Just Dream.</p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                onClick={() => navigate("/auth")}
                className="rounded-full px-10 py-7 text-lg font-semibold bg-tamba-blue hover:bg-tamba-blue/90 text-white shadow-xl shadow-tamba-blue/30 transition-all hover:scale-105"
                data-testid="hero-cta-btn"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={handleTryDemo}
                className="rounded-full px-10 py-7 text-lg border-2 border-tamba-teal/50 hover:bg-tamba-teal/10 hover:border-tamba-teal text-tamba-teal"
                data-testid="hero-demo-btn"
              >
                <Sparkles className="mr-2 h-5 w-5" />
                Try Demo
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-4">No sign up required for demo</p>
          </motion.div>
          
          {/* Floating icons */}
          <div className="hidden lg:block">
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute top-32 left-[8%] w-14 h-14 rounded-2xl bg-tamba-blue shadow-lg shadow-tamba-blue/30 flex items-center justify-center"
            >
              <MessageCircle className="w-7 h-7 text-white" />
            </motion.div>
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 3.5, repeat: Infinity }}
              className="absolute top-48 right-[8%] w-14 h-14 rounded-2xl bg-tamba-yellow shadow-lg shadow-tamba-yellow/30 flex items-center justify-center"
            >
              <PieChart className="w-7 h-7 text-black" />
            </motion.div>
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 2.5, repeat: Infinity }}
              className="absolute bottom-32 left-[12%] w-12 h-12 rounded-2xl bg-tamba-coral shadow-lg shadow-tamba-coral/30 flex items-center justify-center"
            >
              <Target className="w-6 h-6 text-white" />
            </motion.div>
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute bottom-24 right-[12%] w-12 h-12 rounded-2xl bg-tamba-teal shadow-lg shadow-tamba-teal/30 flex items-center justify-center"
            >
              <TrendingUp className="w-6 h-6 text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* App Preview Section - How It Works */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              See <span className="text-tamba-coral">Tamba</span> in Action
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Your complete financial command center, beautifully designed for simplicity
            </p>
          </motion.div>

          {/* Phone Mockups Grid */}
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Mockup 1 - Dashboard */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex flex-col items-center"
            >
              <div className="relative mb-6">
                {/* Phone Frame */}
                <div className="w-64 h-[520px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-slate-900/30">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Status Bar */}
                    <div className="h-10 bg-tamba-blue flex items-center justify-between px-6 pt-2">
                      <span className="text-white text-xs font-medium">9:41</span>
                      <div className="w-20 h-6 bg-black rounded-full absolute left-1/2 -translate-x-1/2 top-1"></div>
                      <div className="flex gap-1">
                        <div className="w-4 h-2 bg-white/80 rounded-sm"></div>
                        <div className="w-4 h-2 bg-white/80 rounded-sm"></div>
                      </div>
                    </div>
                    {/* App Content - Dashboard */}
                    <div className="p-4 bg-gradient-to-b from-tamba-blue to-tamba-blue/90">
                      <p className="text-white/70 text-xs">Good morning</p>
                      <p className="text-white font-bold text-lg">Alex Johnson</p>
                      <div className="mt-4 bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                        <p className="text-white/60 text-xs mb-1">Net Worth</p>
                        <p className="text-white font-bold text-2xl">$294,804</p>
                        <div className="flex items-center mt-1">
                          <TrendingUp className="w-3 h-3 text-green-400 mr-1" />
                          <span className="text-green-400 text-xs font-medium">+12.4% this month</span>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 -mt-2">
                      <div className="bg-white rounded-2xl shadow-lg p-4 border border-slate-100">
                        <p className="text-xs font-semibold text-slate-500 mb-3">QUICK ACTIONS</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="flex flex-col items-center">
                            <div className="w-10 h-10 rounded-full bg-tamba-blue/10 flex items-center justify-center mb-1">
                              <MessageCircle className="w-5 h-5 text-tamba-blue" />
                            </div>
                            <span className="text-[10px] text-slate-600">AI Chat</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <div className="w-10 h-10 rounded-full bg-tamba-teal/10 flex items-center justify-center mb-1">
                              <Target className="w-5 h-5 text-tamba-teal" />
                            </div>
                            <span className="text-[10px] text-slate-600">Goals</span>
                          </div>
                          <div className="flex flex-col items-center">
                            <div className="w-10 h-10 rounded-full bg-tamba-coral/10 flex items-center justify-center mb-1">
                              <PieChart className="w-5 h-5 text-tamba-coral" />
                            </div>
                            <span className="text-[10px] text-slate-600">Budget</span>
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 bg-gradient-to-r from-tamba-yellow/20 to-tamba-yellow/5 rounded-2xl p-3 border border-tamba-yellow/30">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-tamba-yellow" />
                          <p className="text-xs font-medium text-slate-700">AI Insight: You're on track to save $8K more this year!</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating Badge */}
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute -right-4 top-20 bg-tamba-blue text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg"
                >
                  Dashboard
                </motion.div>
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">Smart Dashboard</h3>
              <p className="text-muted-foreground text-center text-sm max-w-xs">
                See your complete financial picture at a glance with real-time insights
              </p>
            </motion.div>

            {/* Mockup 2 - AI Chat */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="flex flex-col items-center lg:-mt-8"
            >
              <div className="relative mb-6">
                {/* Phone Frame */}
                <div className="w-64 h-[520px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-slate-900/30">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Status Bar */}
                    <div className="h-10 bg-white flex items-center justify-between px-6 pt-2 border-b">
                      <span className="text-slate-600 text-xs font-medium">9:41</span>
                      <div className="w-20 h-6 bg-black rounded-full absolute left-1/2 -translate-x-1/2 top-1"></div>
                      <div className="flex gap-1">
                        <div className="w-4 h-2 bg-slate-400 rounded-sm"></div>
                      </div>
                    </div>
                    {/* App Content - AI Chat */}
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-tamba-blue to-tamba-teal flex items-center justify-center">
                          <Sparkles className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Tamba AI</p>
                          <p className="text-xs text-green-500">● Online</p>
                        </div>
                      </div>
                      {/* Chat Messages */}
                      <div className="space-y-3">
                        <div className="bg-slate-100 rounded-2xl rounded-tl-md p-3 max-w-[85%]">
                          <p className="text-xs text-slate-700">How can I save more money this month?</p>
                        </div>
                        <div className="bg-gradient-to-r from-tamba-blue to-tamba-blue/90 rounded-2xl rounded-tr-md p-3 max-w-[85%] ml-auto text-white">
                          <p className="text-xs">Based on your spending, I found 3 ways to save $340 this month:</p>
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              <span className="text-[10px]">Switch to annual subscriptions (-$85)</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              <span className="text-[10px]">Reduce dining out (-$150)</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              <span className="text-[10px]">Cancel unused gym (-$65)</span>
                            </div>
                          </div>
                        </div>
                        <div className="bg-slate-100 rounded-2xl rounded-tl-md p-3 max-w-[85%]">
                          <p className="text-xs text-slate-700">Show me my investment options</p>
                        </div>
                      </div>
                    </div>
                    {/* Input Bar */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-slate-100 rounded-full px-4 py-3 flex items-center gap-2">
                        <input className="bg-transparent text-xs flex-1 outline-none" placeholder="Ask Tamba anything..." readOnly />
                        <div className="w-8 h-8 rounded-full bg-tamba-blue flex items-center justify-center">
                          <ArrowRight className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating Badge */}
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                  className="absolute -left-4 top-32 bg-tamba-teal text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg"
                >
                  AI Advisor
                </motion.div>
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">AI Financial Advisor</h3>
              <p className="text-muted-foreground text-center text-sm max-w-xs">
                Get personalized advice 24/7 from your AI wealth coach
              </p>
            </motion.div>

            {/* Mockup 3 - Goals & Projections */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex flex-col items-center"
            >
              <div className="relative mb-6">
                {/* Phone Frame */}
                <div className="w-64 h-[520px] bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-slate-900/30">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Status Bar */}
                    <div className="h-10 bg-white flex items-center justify-between px-6 pt-2 border-b">
                      <span className="text-slate-600 text-xs font-medium">9:41</span>
                      <div className="w-20 h-6 bg-black rounded-full absolute left-1/2 -translate-x-1/2 top-1"></div>
                    </div>
                    {/* App Content - Goals */}
                    <div className="p-4">
                      <h4 className="font-bold text-lg mb-1">Your Goals</h4>
                      <p className="text-xs text-muted-foreground mb-4">Track your financial dreams</p>
                      
                      {/* Goal Cards */}
                      <div className="space-y-3">
                        <div className="bg-gradient-to-r from-tamba-teal/10 to-tamba-teal/5 rounded-xl p-3 border border-tamba-teal/20">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-tamba-teal/20 flex items-center justify-center">
                                <Target className="w-4 h-4 text-tamba-teal" />
                              </div>
                              <div>
                                <p className="font-semibold text-xs">House Down Payment</p>
                                <p className="text-[10px] text-muted-foreground">26 months left</p>
                              </div>
                            </div>
                          </div>
                          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-tamba-teal rounded-full" style={{width: '35%'}}></div>
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-[10px] text-muted-foreground">$35,000</span>
                            <span className="text-[10px] font-medium text-tamba-teal">$100,000</span>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-tamba-coral/10 to-tamba-coral/5 rounded-xl p-3 border border-tamba-coral/20">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-tamba-coral/20 flex items-center justify-center">
                                <Shield className="w-4 h-4 text-tamba-coral" />
                              </div>
                              <div>
                                <p className="font-semibold text-xs">Emergency Fund</p>
                                <p className="text-[10px] text-muted-foreground">5 months left</p>
                              </div>
                            </div>
                          </div>
                          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-tamba-coral rounded-full" style={{width: '73%'}}></div>
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-[10px] text-muted-foreground">$22,000</span>
                            <span className="text-[10px] font-medium text-tamba-coral">$30,000</span>
                          </div>
                        </div>

                        <div className="bg-gradient-to-r from-tamba-yellow/10 to-tamba-yellow/5 rounded-xl p-3 border border-tamba-yellow/30">
                          <div className="flex items-center gap-2 mb-2">
                            <Zap className="w-4 h-4 text-tamba-yellow" />
                            <p className="font-semibold text-xs">12-Month Projection</p>
                          </div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xl font-bold text-slate-800">$294,804</span>
                            <span className="text-xs text-green-500 font-medium">+142%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating Badge */}
                <motion.div
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
                  className="absolute -right-4 bottom-40 bg-tamba-coral text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg"
                >
                  Goal Tracker
                </motion.div>
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">Goal-Based Planning</h3>
              <p className="text-muted-foreground text-center text-sm max-w-xs">
                Track progress and see exactly when you'll achieve your dreams
              </p>
            </motion.div>
          </div>

          {/* Feature Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
          >
            {[
              { icon: Shield, label: "Bank-Level Security", color: "text-slate-600" },
              { icon: Globe, label: "Multi-Currency", color: "text-tamba-blue" },
              { icon: Smartphone, label: "Works on All Devices", color: "text-tamba-teal" },
              { icon: Zap, label: "Real-Time Sync", color: "text-tamba-yellow" },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-white rounded-full shadow-sm border">
                <item.icon className={`w-4 h-4 ${item.color}`} />
                <span className="text-sm font-medium text-slate-700">{item.label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Everything You Need to
              <span className="text-tamba-blue"> Master</span> Your Money
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              Powerful tools and AI insights designed to transform your financial life
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full ${feature.color} border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group`}>
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 rounded-2xl ${feature.iconBg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <feature.icon className="h-7 w-7" />
                    </div>
                    <h3 className="font-heading text-xl font-bold mb-2">{feature.title}</h3>
                    <p className="opacity-90 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900/50 dark:to-background" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              <span className="text-tamba-teal">10</span> Services to Secure Your{" "}
              <span className="text-tamba-blue">Financial Future</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
              Comprehensive financial advice across every critical area of your wealth journey
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
            {[
              { icon: CreditCard, title: "Debt Management", id: "debt-management", color: "bg-red-500", lightBg: "group-hover:bg-red-50 dark:group-hover:bg-red-950/30" },
              { icon: ShieldCheck, title: "Insurance & Risk", id: "insurance-risk", color: "bg-tamba-blue", lightBg: "group-hover:bg-blue-50 dark:group-hover:bg-blue-950/30" },
              { icon: TrendingUp, title: "Investments", id: "investments", color: "bg-tamba-teal", lightBg: "group-hover:bg-teal-50 dark:group-hover:bg-teal-950/30" },
              { icon: PiggyBank, title: "Pension Funds", id: "pension-funds", color: "bg-pink-500", lightBg: "group-hover:bg-pink-50 dark:group-hover:bg-pink-950/30" },
              { icon: Landmark, title: "Retirement Income", id: "retirement-income", color: "bg-amber-500", lightBg: "group-hover:bg-amber-50 dark:group-hover:bg-amber-950/30" },
              { icon: HeartHandshake, title: "Aged Care", id: "aged-care", color: "bg-purple-500", lightBg: "group-hover:bg-purple-50 dark:group-hover:bg-purple-950/30" },
              { icon: Home, title: "Estate Planning", id: "estate-planning", color: "bg-indigo-500", lightBg: "group-hover:bg-indigo-50 dark:group-hover:bg-indigo-950/30" },
              { icon: Calculator, title: "Cashflow & Budget", id: "cashflow-budgeting", color: "bg-tamba-coral", lightBg: "group-hover:bg-orange-50 dark:group-hover:bg-orange-950/30" },
              { icon: FileText, title: "Tax Planning", id: "taxation-planning", color: "bg-emerald-500", lightBg: "group-hover:bg-emerald-50 dark:group-hover:bg-emerald-950/30" },
              { icon: Briefcase, title: "Salary Packaging", id: "salary-packaging", color: "bg-cyan-600", lightBg: "group-hover:bg-cyan-50 dark:group-hover:bg-cyan-950/30" },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="group"
                data-testid={`landing-service-${i}`}
              >
                <Card className={`h-full border border-border/50 ${item.lightBg} bg-card hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer`}
                  onClick={() => navigate(`/services/${item.id}`)}
                >
                  <CardContent className="p-5 flex flex-col items-center text-center">
                    <div className={`w-12 h-12 rounded-xl ${item.color} flex items-center justify-center mb-3 shadow-md group-hover:scale-110 transition-transform`}>
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground leading-tight">{item.title}</h3>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <Button
              onClick={() => navigate("/services")}
              variant="outline"
              className="rounded-full px-8 py-5 border-2 border-tamba-teal/50 hover:bg-tamba-teal/10 hover:border-tamba-teal text-tamba-teal font-semibold"
              data-testid="landing-view-all-services-btn"
            >
              View All Services
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent via-tamba-blue/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Simple, <span className="text-tamba-yellow">Transparent</span> Pricing
            </h2>
            <p className="text-muted-foreground text-lg">
              Start free, upgrade when you're ready
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Monthly */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-border/50 hover:border-tamba-blue/50 transition-all hover:shadow-xl bg-card">
                <CardContent className="p-8">
                  <div className="mb-6">
                    <h3 className="font-heading text-2xl font-bold mb-2">Monthly</h3>
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-5xl font-bold text-tamba-blue">$9.99</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {["AI Financial Advisor", "Unlimited Budget Tracking", "Goal Setting & Tracking", "Spending Insights", "Priority Support"].map((item, i) => (
                      <li key={item} className="flex items-center gap-3 text-sm">
                        <div className="w-5 h-5 rounded-full bg-tamba-teal/20 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3 w-3 text-tamba-teal" />
                        </div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => navigate("/auth")} 
                    className="w-full rounded-full py-6 border-2 border-tamba-blue text-tamba-blue bg-transparent hover:bg-tamba-blue hover:text-white transition-all"
                    variant="outline"
                    data-testid="monthly-plan-btn"
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Annual */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-tamba-yellow bg-gradient-to-br from-tamba-yellow/10 to-tamba-coral/5 relative shadow-xl shadow-tamba-yellow/20">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-tamba-yellow to-tamba-coral text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                    Save 33%
                  </span>
                </div>
                <CardContent className="p-8 pt-10">
                  <div className="mb-6">
                    <h3 className="font-heading text-2xl font-bold mb-2">Annual</h3>
                    <div className="flex items-baseline gap-1">
                      <span className="font-heading text-5xl font-bold text-tamba-yellow">$79.99</span>
                      <span className="text-muted-foreground">/year</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">~$6.67/month</p>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {["Everything in Monthly", "2 Months Free", "Annual Financial Review", "Export Reports", "Early Access to Features"].map((item, i) => (
                      <li key={item} className="flex items-center gap-3 text-sm">
                        <div className="w-5 h-5 rounded-full bg-tamba-teal/20 flex items-center justify-center flex-shrink-0">
                          <Check className="h-3 w-3 text-tamba-teal" />
                        </div>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => navigate("/auth")} 
                    className="w-full rounded-full py-6 bg-gradient-to-r from-tamba-yellow to-tamba-coral hover:opacity-90 text-white font-bold shadow-lg"
                    data-testid="annual-plan-btn"
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Trusted by <span className="text-tamba-teal">Thousands</span> Worldwide
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-card border-border/50 hover:shadow-xl hover:border-tamba-blue/30 transition-all">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="text-tamba-yellow text-lg">★</span>
                      ))}
                    </div>
                    <p className="text-foreground mb-6 italic leading-relaxed">"{testimonial.quote}"</p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-tamba-blue to-tamba-teal flex items-center justify-center text-white font-bold">
                        {testimonial.avatar}
                      </div>
                      <div>
                        <p className="font-semibold">{testimonial.author}</p>
                        <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="rounded-2xl bg-tamba-blue overflow-hidden shadow-2xl shadow-tamba-blue/30 relative">
              <div className="p-12 md:p-16 text-center text-white relative">
                {/* Decorative shapes */}
                <div className="absolute top-0 left-0 w-32 h-32 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
                <div className="absolute bottom-0 right-0 w-48 h-48 bg-white/10 rounded-full translate-x-1/4 translate-y-1/4" />
                
                <div className="relative">
                  <Zap className="w-12 h-12 mx-auto mb-6 text-tamba-yellow" />
                  <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-white">
                    Ready to Transform Your Finances?
                  </h2>
                  <p className="text-white/90 mb-4 text-lg max-w-2xl mx-auto">
                    Join thousands of users who are building wealth with Tamba
                  </p>
                  <p className="text-tamba-yellow font-medium tracking-widest mb-8">JUST DREAM.</p>
                  <Button 
                    size="lg" 
                    onClick={() => navigate("/auth")}
                    className="rounded-full px-12 py-7 text-lg font-bold bg-white text-tamba-blue hover:bg-white/90 shadow-xl transition-all hover:scale-105"
                    data-testid="footer-cta-btn"
                  >
                    Start Your Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-border/30 bg-gradient-to-b from-transparent to-secondary/30">
        <div className="max-w-7xl mx-auto">
          {/* Main Footer Content */}
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            {/* Brand Column */}
            <div className="md:col-span-1">
              <TambaLogo className="h-12 w-auto mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                Your AI-powered financial coach. Build wealth intelligently.
              </p>
              <p className="text-tamba-teal font-medium text-sm tracking-wider">JUST DREAM.</p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button onClick={() => navigate("/services")} className="hover:text-tamba-blue transition-colors">Services</button></li>
                <li><button onClick={() => navigate("/pricing")} className="hover:text-tamba-blue transition-colors">Pricing</button></li>
                <li><button onClick={() => navigate("/auth")} className="hover:text-tamba-blue transition-colors">Get Started</button></li>
                <li><span className="cursor-default">AI Advisor</span></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-heading font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><span className="cursor-default">About Us</span></li>
                <li><span className="cursor-default">Careers</span></li>
                <li><span className="cursor-default">Privacy Policy</span></li>
                <li><span className="cursor-default">Terms of Service</span></li>
              </ul>
            </div>

            {/* Download App */}
            <div>
              <h4 className="font-heading font-semibold mb-4">Get the App</h4>
              <div className="space-y-3">
                {/* App Store Button */}
                <a 
                  href="https://apps.apple.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-black text-white rounded-xl px-4 py-3 hover:bg-black/80 transition-colors group"
                  data-testid="app-store-btn"
                >
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div>
                    <p className="text-[10px] opacity-80">Download on the</p>
                    <p className="text-sm font-semibold -mt-0.5">App Store</p>
                  </div>
                </a>

                {/* Google Play Button */}
                <a 
                  href="https://play.google.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-black text-white rounded-xl px-4 py-3 hover:bg-black/80 transition-colors group"
                  data-testid="google-play-btn"
                >
                  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                  </svg>
                  <div>
                    <p className="text-[10px] opacity-80">GET IT ON</p>
                    <p className="text-sm font-semibold -mt-0.5">Google Play</p>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-border/30 flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Social Media Links */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground mr-2">Follow us:</span>
              <a 
                href="https://twitter.com/tamba" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-secondary hover:bg-tamba-blue hover:text-white flex items-center justify-center transition-all"
                data-testid="social-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com/tamba" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-secondary hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:text-white flex items-center justify-center transition-all"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://facebook.com/tamba" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-secondary hover:bg-blue-600 hover:text-white flex items-center justify-center transition-all"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://linkedin.com/company/tamba" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-secondary hover:bg-blue-700 hover:text-white flex items-center justify-center transition-all"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://youtube.com/@tamba" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-secondary hover:bg-red-600 hover:text-white flex items-center justify-center transition-all"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>

            {/* Copyright */}
            <p className="text-sm text-muted-foreground">
              © 2026 Tamba. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
